import { Component } from './Component';
import type { AncestorGraph } from '../domain/AncestorGraph';
import type { LineagePath } from '../domain/LineagePath';
import { AppStore } from '../store/AppStore';
import { SelectionStore } from '../store/SelectionStore';
import { createLogger } from '../utils/logger';

const log = createLogger('BranchPanel');
const UNKNOWN_TITLE = 'No ancestor found at this branch position';

/** "paternal-maternal-paternal" → "Father › Mother › Father" */
export function branchLabel(branchId: string): string {
  return branchId
    .split('-')
    .map((seg) => (seg === 'paternal' ? 'Father' : seg === 'maternal' ? 'Mother' : seg))
    .join(' › ');
}

/**
 * Display name of a branch: its great-grandparent (personIds[3] — root → parent →
 * grandparent → great-grandparent), since a branch is all ancestors of that person.
 * Falls back to the path label when the path does not reach generation 3.
 * Used by both BranchPanel and the DotLayer tooltip.
 */
export function branchDisplayName(path: LineagePath, graph: AncestorGraph | null): string {
  if (path.personIds.length < 4) {
    return branchLabel(path.branchId);
  }
  const ggp = graph?.persons.get(path.personIds[3] ?? '');
  const name = ggp !== undefined ? `${ggp.name.first} ${ggp.name.last}`.trim() : '';
  return name || '(unknown name)';
}

export class BranchPanel extends Component {
  private listEl!: HTMLUListElement;
  private probandEl!: HTMLParagraphElement;

  render(): HTMLElement {
    const el = document.createElement('div');
    el.className = 'branch-panel';
    el.setAttribute('data-testid', 'branch-panel');
    el.style.display = 'none';
    el.innerHTML = `
      <h3 class="branch-panel-heading">Branches</h3>
      <p class="branch-proband" data-testid="branch-proband"></p>
      <ul data-testid="branch-list"></ul>
    `;
    this.listEl = el.querySelector('ul')!;
    this.probandEl = el.querySelector('[data-testid="branch-proband"]')!;
    return el;
  }

  mount(parent: HTMLElement): void {
    super.mount(parent);
    this.track(AppStore.subscribe(() => this._update()));
    this.track(SelectionStore.subscribe(() => this._update()));
    this._update();
    log.info('BranchPanel mounted');
  }

  private _update(): void {
    const { branchPaths, graph, branchAssignments, dotMap } = AppStore.getState();
    const { activeBranchId } = SelectionStore.getState();

    if (branchPaths.length === 0) {
      this.el.style.display = 'none';
      return;
    }
    this.el.style.display = '';

    // Proband display
    const proband = graph?.persons.get(graph.rootId);
    const probandName = proband
      ? `${proband.name.first} ${proband.name.last}`.trim() || '(unknown)'
      : '';
    this.probandEl.textContent = probandName ? `Ancestors of ${probandName}` : '';

    const ancestorCounts = new Map<string, number>();
    for (const branchId of branchAssignments.values()) {
      ancestorCounts.set(branchId, (ancestorCounts.get(branchId) ?? 0) + 1);
    }

    this.listEl.innerHTML = '';
    for (let i = 0; i < 8; i++) {
      const path = branchPaths[i];
      const li = document.createElement('li');
      li.className = `branch-slot${path && activeBranchId === path.branchId ? ' active' : ''}`;
      li.setAttribute('data-testid', `branch-slot-${i}`);

      if (path) {
        const displayName = branchDisplayName(path, graph);
        const isActive = activeBranchId === path.branchId;

        const swatchEl = document.createElement('span');
        swatchEl.className = 'branch-swatch';
        swatchEl.setAttribute('aria-hidden', 'true');
        swatchEl.style.background = path.branchColor;

        const nameSpan = document.createElement('span');
        nameSpan.className = 'branch-name';
        nameSpan.title = `${branchLabel(path.branchId)} — great-grandparent`;
        nameSpan.textContent = displayName;

        const countSpan = document.createElement('span');
        countSpan.className = 'branch-count';
        countSpan.title = 'ancestors in this branch';
        countSpan.textContent = (ancestorCounts.get(path.branchId) ?? 0).toString();

        const btn = document.createElement('button');
        btn.className = 'branch-isolate-btn';
        btn.setAttribute('data-testid', `branch-isolate-${i}`);
        btn.setAttribute('data-branch-id', path.branchId);
        btn.setAttribute(
          'aria-label',
          isActive ? `Clear ${displayName} branch isolation` : `Isolate ${displayName} branch`,
        );
        btn.textContent = isActive ? 'Clear' : 'Isolate';
        btn.addEventListener('click', () => {
          SelectionStore.setState({ activeBranchId: isActive ? null : path.branchId });
        });

        li.appendChild(swatchEl);
        li.appendChild(nameSpan);
        li.appendChild(countSpan);
        li.appendChild(btn);

        // Isolating a branch with nothing on the map would otherwise just gray everything.
        if (isActive && !hasMappedPlaces(dotMap, path.branchId)) {
          const note = document.createElement('p');
          note.className = 'branch-empty-note';
          note.setAttribute('role', 'status');
          note.textContent = 'No mapped places for this branch yet.';
          li.appendChild(note);
        }
      } else {
        // Unknown slot
        li.title = UNKNOWN_TITLE;
        li.innerHTML = `
          <span class="branch-swatch" aria-hidden="true" style="background:#999"></span>
          <span class="branch-name unknown">Unknown</span>
          <button class="branch-isolate-btn" data-testid="branch-isolate-${i}" disabled
                  aria-label="Isolate — no ancestor found at this branch position">
            Isolate
          </button>
        `;
      }

      this.listEl.appendChild(li);
    }
  }
}

function hasMappedPlaces(
  dotMap: Map<string, { branchCounts: Map<string, number> }>,
  branchId: string,
): boolean {
  for (const cell of dotMap.values()) {
    if (cell.branchCounts.has(branchId)) {
      return true;
    }
  }
  return false;
}
